import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
    LayoutDashboard,
    Users,
    Building2,
    Layers,
    Activity,
    Mail,
    Megaphone,
    Database,
    ScrollText,
    ShieldCheck,
    MoreHorizontal,
    X,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { ADMIN_TABS, ADMIN_PRIMARY_MOBILE_KEYS, adminTabLocked, resolveAdminTab } from "@/lib/adminNav";

const TAB_ICONS = {
    dashboard: LayoutDashboard,
    users: Users,
    orgs: Building2,
    pools: Layers,
    traffic: Activity,
    emails: Mail,
    site: Megaphone,
    database: Database,
    audit: ScrollText,
    security: ShieldCheck,
};

function AdminNavLink({ tab, needs2fa, onNavigate, className, children, showRequired }) {
    const locked = adminTabLocked(needs2fa, tab.key);

    if (locked) {
        return (
            <span className={`${className} pointer-events-none opacity-35`} aria-disabled="true">
                {children}
            </span>
        );
    }

    return (
        <NavLink
            to={tab.to}
            onClick={onNavigate}
            className={({ isActive }) =>
                `${className}${isActive ? " admin-mobile-nav__link--active" : ""}`
            }
            data-testid={`admin-nav-${tab.label.toLowerCase().replace(/\s+/g, "-")}`}
        >
            {children}
            {showRequired && tab.key === "security" ? (
                <span className="admin-mobile-nav__required">!</span>
            ) : null}
        </NavLink>
    );
}

/** Mobile-only bottom navigation + overflow sheet for superadmin console. */
export function AdminMobileNav() {
    const { pathname } = useLocation();
    const { user } = useAuth();
    const needs2fa = !user?.totp_enabled;
    const [moreOpen, setMoreOpen] = useState(false);
    const current = resolveAdminTab(pathname);

    const primaryTabs = ADMIN_TABS.filter((t) => ADMIN_PRIMARY_MOBILE_KEYS.has(t.key));
    const overflowTabs = ADMIN_TABS.filter((t) => !ADMIN_PRIMARY_MOBILE_KEYS.has(t.key));
    const moreActive = overflowTabs.some((t) => t.key === current.key);

    useEffect(() => {
        setMoreOpen(false);
    }, [pathname]);

    useEffect(() => {
        if (!moreOpen) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [moreOpen]);

    const closeMore = () => setMoreOpen(false);

    return (
        <>
            <nav
                className="admin-mobile-nav lg:hidden"
                aria-label="Admin sections"
                data-testid="admin-mobile-nav"
            >
                {primaryTabs.map((tab) => {
                    const Icon = TAB_ICONS[tab.key];
                    return (
                        <AdminNavLink
                            key={tab.key}
                            tab={tab}
                            needs2fa={needs2fa}
                            className="admin-mobile-nav__link"
                        >
                            <Icon className="admin-mobile-nav__icon" aria-hidden />
                            <span className="admin-mobile-nav__label">{tab.shortLabel}</span>
                        </AdminNavLink>
                    );
                })}
                <button
                    type="button"
                    onClick={() => setMoreOpen(true)}
                    className={`admin-mobile-nav__link admin-mobile-nav__more${moreActive ? " admin-mobile-nav__link--active" : ""}`}
                    aria-expanded={moreOpen}
                    data-testid="admin-mobile-nav-more"
                >
                    <MoreHorizontal className="admin-mobile-nav__icon" aria-hidden />
                    <span className="admin-mobile-nav__label">More</span>
                </button>
            </nav>

            {moreOpen ? (
                <div className="admin-mobile-sheet" role="dialog" aria-modal="true" aria-label="More admin sections">
                    <button
                        type="button"
                        className="admin-mobile-sheet__backdrop"
                        onClick={closeMore}
                        aria-label="Close menu"
                    />
                    <div className="admin-mobile-sheet__panel">
                        <div className="admin-mobile-sheet__header">
                            <p className="admin-mobile-sheet__title">Admin sections</p>
                            <button
                                type="button"
                                onClick={closeMore}
                                className="admin-mobile-sheet__close"
                                aria-label="Close"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="admin-mobile-sheet__grid">
                            {overflowTabs.map((tab) => {
                                const Icon = TAB_ICONS[tab.key];
                                const isActive = current.key === tab.key;
                                return (
                                    <AdminNavLink
                                        key={tab.key}
                                        tab={tab}
                                        needs2fa={needs2fa}
                                        onNavigate={closeMore}
                                        showRequired
                                        className={`admin-mobile-sheet__item${isActive ? " admin-mobile-sheet__item--active" : ""}`}
                                    >
                                        <Icon className="h-5 w-5 shrink-0" aria-hidden />
                                        <span className="min-w-0 truncate">{tab.label}</span>
                                    </AdminNavLink>
                                );
                            })}
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
}
